// Generic deck.gl layer + tooltip for any connector's GeoJSON
// (GET /api/connectors/:id/geojson). One factory for every department's
// source — no per-source map code.
import { GeoJsonLayer } from "@deck.gl/layers";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import {
  connectorLayerId,
  type ConnectorFeatureCollection, type ConnectorFeatureProperties, type ConnectorSummary, type FallbackTier,
} from "@chula/shared";
import { TIER_TAG } from "../lib/tierDisplay";

export const DEFAULT_CONNECTOR_COLOR = "#64748B";

/** One map-able connector, as the palette / legend / layer factory see it. */
export interface ConnectorLayerEntry {
  layerId: `connector:${string}`;
  connectorId: string;
  label: string;
  color: string;
  /** Tier the feed is served with right now. */
  tier: FallbackTier;
  describe: string;
  units: Record<string, string>;
}

const TIER_WORDS: Record<FallbackTier, string> = {
  live: "live data from the source",
  database: "our own stored records",
  cache: "last known data — the source is stale",
  scenario: "modeled estimate, not a measurement",
  reference: "published reference data, updated occasionally",
  unavailable: "source offline — nothing current",
};

/** Badge text for a tier. Live is spelled out so the tooltip always names its tier. */
export function tierBadgeText(tier: FallbackTier): string {
  // Same plain words as panel headers (MODEL, CACHED, REF, OFFLINE); live is spelled out.
  return TIER_TAG[tier] || "LIVE";
}

/** Manifest connectors that have a map route, in registry order. Built-ins are drawn by their own layers. */
export function connectorLayerEntries(connectors: readonly ConnectorSummary[]): ConnectorLayerEntry[] {
  return connectors
    .filter((c) => c.origin === "manifest" && c.enabled && c.geojsonPath !== null)
    .map((c) => ({
      layerId: connectorLayerId(c.id),
      connectorId: c.id,
      label: c.display?.label ?? c.name,
      color: c.display?.color ?? DEFAULT_CONNECTOR_COLOR,
      tier: c.servedTier,
      describe: `${c.name} — ${c.owner}. ${tierBadgeText(c.servedTier)}: ${TIER_WORDS[c.servedTier]}.`,
      units: c.display?.units ?? {},
    }));
}

export function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  const n = parseInt(m ? m[1] : DEFAULT_CONNECTOR_COLOR.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Fill alpha by tier: honest data draws solid, degraded data visibly fainter. */
function alphaFor(tier: FallbackTier): number {
  if (tier === "live" || tier === "database") return 220;
  if (tier === "reference") return 200;
  return 130; // cache, scenario, unavailable
}

export function createConnectorLayer(entry: ConnectorLayerEntry, collection: ConnectorFeatureCollection) {
  const [r, g, b] = hexToRgb(entry.color);
  const a = alphaFor(collection.meta?.fallbackTier ?? entry.tier);
  return new GeoJsonLayer<ConnectorFeatureProperties>({
    id: entry.layerId,
    data: collection as unknown as FeatureCollection<Geometry, ConnectorFeatureProperties>,
    pickable: true,
    stroked: true,
    filled: true,
    pointType: "circle",
    getFillColor: (f: Feature<Geometry, ConnectorFeatureProperties>) =>
      f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon" ? [r, g, b, 60] : [r, g, b, a],
    getLineColor: [r, g, b, 255],
    getLineWidth: 2,
    lineWidthMinPixels: 1,
    getPointRadius: 6,
    pointRadiusUnits: "pixels",
    pointRadiusMinPixels: 4,
    updateTriggers: { getFillColor: [entry.color, a] },
  });
}

function formatAge(observedAt: string | null, now: number): string {
  if (!observedAt) return "observed: time unknown";
  const t = Date.parse(observedAt);
  if (!Number.isFinite(t)) return "observed: time unknown";
  const min = Math.max(0, Math.round((now - t) / 60_000));
  if (min < 1) return "observed just now";
  if (min < 60) return `observed ${min} min ago`;
  if (min < 48 * 60) return `observed ${Math.round(min / 60)} h ago`;
  return `observed ${Math.round(min / 1440)} d ago`;
}

function formatMetric(value: number | string | null, unit: string | undefined): string {
  if (value === null) return "—";
  const v = typeof value === "number" ? String(Math.round(value * 100) / 100) : value;
  return unit ? `${v} ${unit}` : v;
}

/**
 * Plain-text tooltip (deck.gl renders `text` with innerText — no HTML
 * injection from upstream strings). Always names the tier; anything below
 * live says what it is.
 */
export function formatConnectorTooltip(
  props: ConnectorFeatureProperties,
  entry: Pick<ConnectorLayerEntry, "label" | "units">,
  now: number = Date.now(),
): { text: string } {
  const lines = [props.label ?? props.id, entry.label];
  for (const [k, v] of Object.entries(props.metrics ?? {})) {
    lines.push(`${k}: ${formatMetric(v, entry.units[k])}`);
  }
  lines.push(formatAge(props.observedAt, now));
  lines.push(`${tierBadgeText(props.tier)} · ${TIER_WORDS[props.tier]}`);
  return { text: lines.join("\n") };
}
